import { useCallback } from "react";
import { z } from "zod";
import {
  Box,
  TabsViewIcon,
  Text,
  WidgetEmpty,
  WidgetListRow,
  type WidgetModule,
  type WidgetRenderContext,
  type WidgetSettingsGroup,
} from "@salestrak/ui";
import { EntityTag } from "#app/components/entity";
import { useIssueView } from "#app/components/issue-view";
import { useWorkspace } from "#app/lib/data/workspace-context";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { issueHref, routeHref } from "#app/lib/utils/entity-hrefs";
import { useCompactBody } from "../shared/compact-body";
import { RowLinkActions } from "../shared/row-link-actions";
import { useRecentList, type RecentListRow } from "./use-recent-list";

/**
 * `recent-list` — the viewer's recently-opened Projects and dashboards in ONE
 * list, newest first. A row is a `WidgetListRow` whose leading chip is the
 * entity's own visual (the project type's icon, the dashboard's view tile), so
 * the kind reads off the glyph and the trailing rail only has to say where.
 * Press re-opens what was opened: the detail overlay for a project,
 * navigation for a dashboard.
 *
 * A **managed-only** card (the My Work catalog) — it contributes no `catalog`
 * entry, so it never appears in the free Add-card gallery. Its emptiness is
 * not about the window (nothing here is scoped), so it keeps its own icon and
 * description rather than the window empty title.
 */
export const recentListConfigSchema = z.looseObject({
  limit: z.number().int().min(3).max(20).default(10),
});

export type RecentListConfig = z.infer<typeof recentListConfigSchema>;

/** The row's leading identity: the entity chip, inert because the row owns
 *  the press. A dashboard is not an entity kind, so it wears the same view
 *  tile + name pair the Dashboards module menu draws for it. */
function RecentRowIdentity({ row }: { row: RecentListRow }) {
  switch (row.kind) {
    case "issue":
      return (
        <EntityTag
          display="full"
          entity={{
            kind: "issue",
            id: row.item.id,
            issue: {
              name: row.item.name,
              icon: { icon: row.item.iconName, color: row.item.iconColor },
            },
            workflowId: row.item.workflowId ?? undefined,
          }}
          interactive={false}
          weight="regular"
        />
      );
    case "dashboard":
      return (
        <Box align="center" direction="row" gap="sm" minWidth={0}>
          <TabsViewIcon color={row.dashboard.color} icon={row.dashboard.icon} />
          <Text truncate>{row.dashboard.name}</Text>
        </Box>
      );
  }
}

/** Where the row lives, as a place the reader could go: a project in its
 *  workflow, a dashboard among the dashboards. Words, no glyph — the identity
 *  already carries one. */
function rowLocation(row: RecentListRow, workflowName: (id: string) => string | undefined) {
  switch (row.kind) {
    case "issue":
      return row.item.workflowId ? workflowName(row.item.workflowId) : undefined;
    case "dashboard":
      return "Dashboards";
  }
}

/** The row's shareable link — the same URL its entity's menu copies. A
 *  project the store no longer knows the workflow of has none. */
function rowHref(row: RecentListRow, workspace: string): string | undefined {
  switch (row.kind) {
    case "issue":
      return row.item.workflowId ? issueHref(workspace, row.item.workflowId, row.item.id) : undefined;
    case "dashboard":
      return routeHref(row.dashboard.route);
  }
}

function RecentListRender({ config }: WidgetRenderContext<RecentListConfig>) {
  const rows = useRecentList(config.limit);
  const { openIssueById } = useIssueView();
  const { push } = useAppNavigate();
  const { activeWorkspace } = useWorkspace();
  const workspace = activeWorkspace?.urlPath ?? "";
  const { workflowMap } = useWorkspaceData();
  const workflowName = useCallback(
    (id: string) => workflowMap.get(id)?.name ?? undefined,
    [workflowMap],
  );
  // A narrow card keeps the name and the time; the place steps out.
  const { ref: bodyRef, compact } = useCompactBody();

  const open = useCallback(
    (row: RecentListRow) => {
      switch (row.kind) {
        case "issue":
          openIssueById(row.item.id, { source: "widget" });
          return;
        case "dashboard":
          push(row.dashboard.route);
      }
    },
    [openIssueById, push],
  );

  if (rows.length === 0) {
    return (
      <WidgetEmpty
        title="Nothing opened yet"
      />
    );
  }

  return (
    <Box ref={bodyRef} direction="column">
      {rows.map((row) => {
        const location = compact ? undefined : rowLocation(row, workflowName);
        const href = workspace ? rowHref(row, workspace) : undefined;
        return (
          <WidgetListRow key={row.key} onClick={() => open(row)}>
            {/* Name · in <location>: the location hugs the name on an `xs`
                gap and never shrinks, so a long name gives way to the
                ellipsis and the place it lives is always said in full. */}
            <Box align="center" direction="row" gap="xs" minWidth={0} shrink>
              <Box minWidth={0} shrink>
                <RecentRowIdentity row={row} />
              </Box>
              {location ? (
                // A flex seat at the chip's own rung (`sm`, a 20px line): a
                // block wrapper ran the page's 24px line box under an inline
                // span and stretched every row to 40 — four taller than the
                // Follow ups rows the board seats beside these.
                <Box align="center" direction="row" shrink={false}>
                  <Text size="sm" variant="muted" truncate={false}>
                    {`· in ${location}`}
                  </Text>
                </Box>
              ) : null}
            </Box>
            {/* The trail is the two link verbs, on hover. No time: the list
                is ordered newest first and says so by its order; a stamp
                per row restated it. No kind word: the leading visual already
                says what kind of thing it is. */}
            {href ? (
              <Box align="center" direction="row" justify="end" shrink={false}>
                <RowLinkActions href={href} />
              </Box>
            ) : null}
          </WidgetListRow>
        );
      })}
    </Box>
  );
}

const recentListSettings: WidgetSettingsGroup[] = [
  {
    title: "Data",
    fields: [
      { key: "limit", kind: "number", label: "Show", icon: "list", min: 3, max: 20, step: 1 },
    ],
  },
];

export const recentListDef: WidgetModule<RecentListConfig> = {
  type: "recent-list",
  title: "Recent",
  icon: "history",
  // The rows are a personal trail, not a slice of the scoped projects — the
  // host's default dataset would be unrelated, so the Data tab hides.
  drilldownDataset: null,
  span: { minW: 3, minH: 3, maxW: 12, maxH: 12, defaultW: 6 },
  configSchema: recentListConfigSchema,
  settings: recentListSettings,
  render: RecentListRender,
};
